import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const Section = ({ n, title, children }) => (
  <div className="border-t border-brand-border py-8 md:py-10">
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
      <div className="md:col-span-4">
        <div className="text-xs font-display font-semibold uppercase tracking-[0.2em] text-brand-red">
          Section {n}
        </div>
        <div className="font-display font-bold text-brand-ink text-xl md:text-2xl mt-2 leading-tight">
          {title}
        </div>
      </div>
      <div className="md:col-span-8 text-brand-slate leading-relaxed space-y-4">
        {children}
      </div>
    </div>
  </div>
);

const Bullets = ({ items }) => (
  <ul className="list-disc pl-5 space-y-2 marker:text-brand-red">
    {items.map((it) => (
      <li key={it}>{it}</li>
    ))}
  </ul>
);

const Terms = () => {
  return (
    <main className="bg-white text-brand-ink min-h-screen">
      <Header />
      <div className="pt-32 md:pt-36 pb-20 px-5 md:px-10 lg:px-16">
        <Link to="/" className="inline-flex items-center gap-2 text-brand-slate hover:text-brand-blue font-display font-medium">
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        <div className="mt-8 max-w-5xl">
          <div className="text-xs md:text-sm font-display font-semibold tracking-[0.2em] uppercase text-brand-red mb-3">
            Legal
          </div>
          <h1 className="section-heading font-display font-bold text-4xl md:text-6xl leading-tight text-brand-ink">
            Terms &amp; Conditions
          </h1>
          <p className="mt-6 text-sm text-brand-muted">
            Last Updated: {new Date().toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })}
          </p>
          <p className="mt-6 text-base md:text-lg text-brand-slate max-w-3xl leading-relaxed">
            These Terms &amp; Conditions (&ldquo;Terms&rdquo;) govern your access to and use of
            the website, communications, and recruiting, staffing, executive search,
            and Recruitment Process Outsourcing (RPO) services provided by
            SNV Elite Solutions (&ldquo;Company,&rdquo; &ldquo;SNV Elite Solutions,&rdquo;
            &ldquo;we,&rdquo; &ldquo;us,&rdquo; or &ldquo;our&rdquo;). By accessing
            our website, submitting information, or engaging our services, you agree
            to be bound by these Terms. If you do not agree, please do not use our
            website or services.
          </p>
        </div>

        <div className="mt-14 max-w-5xl">
          <Section n="1" title="Acceptance of Terms">
            <p>
              By using our website or services, you confirm that you have read,
              understood, and agree to these Terms and to our{" "}
              <Link to="/privacy" className="text-brand-red font-semibold link-underline">Privacy Policy</Link>,
              which is incorporated by reference. These Terms apply to all
              visitors, candidates, clients, and users of our website and
              services.
            </p>
          </Section>

          <Section n="2" title="Our Services">
            <p>
              SNV Elite Solutions provides recruiting, staffing, executive
              search, and Recruitment Process Outsourcing (RPO) services across
              the United States, Europe, and Canada. We specialize in
              Construction, Healthcare, Engineering, Manufacturing, Information
              Technology, Finance, Accounting, Legal, Blue Collar, Executive
              Leadership, and other high-demand industries. We connect
              candidates with potential employers and assist clients with
              hiring. We may modify, suspend, or discontinue any part of our
              services or website at any time without notice.
            </p>
          </Section>

          <Section n="3" title="Eligibility">
            <p>
              Our website and services are intended for adults and professional
              users who are at least 18 years of age. By using our website or
              services, you represent that you meet this requirement and that
              any information you provide is accurate, current, and complete.
            </p>
          </Section>

          <Section n="4" title="Use of the Website">
            <p>You agree to use our website and services only for lawful purposes. You agree not to:</p>
            <Bullets
              items={[
                "Provide false, misleading, or fraudulent information",
                "Submit resumes or information on behalf of another person without authorization",
                "Attempt to gain unauthorized access to our systems, accounts, or data",
                "Interfere with or disrupt the website, servers, or security features",
                "Use automated tools to scrape, harvest, or collect data from the website",
                "Upload or transmit viruses, malware, or other harmful code",
                "Use our website or services in any way that violates applicable law",
              ]}
            />
          </Section>

          <Section n="5" title="Candidate & Client Responsibilities">
            <p>
              Candidates are responsible for the accuracy of the resumes,
              credentials, licenses, and information they submit. Clients are
              responsible for their own hiring decisions and for compliance
              with applicable employment laws. SNV Elite Solutions acts as an
              intermediary and is not the employer of candidates placed with
              clients unless expressly stated in a separate written agreement.
            </p>
          </Section>

          <Section n="6" title="No Guarantee of Employment or Placement">
            <p>
              Submitting information or applying through our website does not
              guarantee employment, placement, an interview, or any particular
              outcome. Any staffing guarantees, replacement terms, or fees are
              governed solely by a separate written agreement between SNV Elite
              Solutions and the applicable client.
            </p>
          </Section>

          <Section n="7" title="Intellectual Property">
            <p>
              All content on this website — including text, graphics, logos,
              images, and design — is the property of SNV Elite Solutions or
              its licensors and is protected by intellectual property laws.
              You may not copy, reproduce, distribute, or create derivative
              works from our content without our prior written permission.
            </p>
          </Section>

          <Section n="8" title="Third-Party Links & Services">
            <p>
              Our website may link to third-party websites, job boards, or
              services (such as LinkedIn Recruiter, Indeed, or our careers
              portal). We are not responsible for the content, policies, or
              practices of those third parties. Your use of any third-party
              service is at your own risk and subject to that third
              party&apos;s terms.
            </p>
          </Section>

          <Section n="9" title="Email & SMS Communications">
            <p>
              By voluntarily providing your phone number or email address, you
              consent to receive business, recruiting, and follow-up
              communications from SNV Elite Solutions. Message frequency may
              vary, and message and data rates may apply. You may opt out at
              any time by replying <strong>STOP</strong> to text messages,
              using unsubscribe links where available, or contacting us
              directly. Reply <strong>HELP</strong> for assistance. Consent is
              not a condition of employment or of any purchase. Additional
              details are described in our{" "}
              <Link to="/privacy" className="text-brand-red font-semibold link-underline">
                Privacy Policy
              </Link>.
            </p>
          </Section>

          <Section n="10" title="Disclaimers">
            <p>
              Our website and services are provided &ldquo;as is&rdquo; and
              &ldquo;as available&rdquo; without warranties of any kind,
              whether express or implied, including warranties of
              merchantability, fitness for a particular purpose, and
              non-infringement. We do not warrant that the website will be
              uninterrupted, error-free, or free of harmful components, or
              that any information provided is complete or accurate.
            </p>
          </Section>

          <Section n="11" title="Limitation of Liability">
            <p>
              To the fullest extent permitted by law, SNV Elite Solutions and
              its officers, employees, and agents will not be liable for any
              indirect, incidental, special, consequential, or punitive
              damages, or for any loss of profits, data, or goodwill, arising
              out of or relating to your use of the website or services, even
              if advised of the possibility of such damages.
            </p>
          </Section>

          <Section n="12" title="Indemnification">
            <p>
              You agree to indemnify and hold harmless SNV Elite Solutions and
              its officers, employees, and agents from any claims, damages,
              liabilities, and expenses (including reasonable attorneys&apos;
              fees) arising out of your use of the website or services, your
              violation of these Terms, or your violation of any applicable
              law or the rights of a third party.
            </p>
          </Section>

          <Section n="13" title="Governing Law">
            <p>
              These Terms are governed by and construed in accordance with the
              laws of India for services delivered from our headquarters in
              Udaipur, Rajasthan, and by the laws of the State of Texas,
              United States, for services delivered from our Dallas office,
              in each case without regard to conflict-of-law principles. Any
              dispute arising under these Terms shall be subject to the
              exclusive jurisdiction of the courts of the relevant
              jurisdiction.
            </p>
          </Section>

          <Section n="14" title="Changes to These Terms">
            <p>
              We may update these Terms periodically. Updates will be
              reflected by the revised &ldquo;Last Updated&rdquo; date at the
              top of this page. Continued use of our website or services
              after updates constitutes acceptance of the revised Terms.
            </p>
          </Section>

          <Section n="15" title="Contact Information">
            <p>If you have questions about these Terms, please contact us:</p>
            <div className="bg-brand-surface border border-brand-border rounded-xl p-6 mt-2">
              <div className="font-display font-bold text-brand-ink text-lg">SNV Elite Solutions</div>
              <div className="mt-3 text-brand-slate space-y-1">
                <div>India Headquarters — Udaipur, Rajasthan, India</div>
                <div>United States Office — Dallas, TX, United States</div>
                <div>
                  Email:{" "}
                  <a
                    href="mailto:Vishan@Snvelite.com"
                    className="text-brand-red font-semibold link-underline"
                  >
                    Vishan@Snvelite.com
                  </a>
                </div>
                <div>
                  Phone:{" "}
                  <a href="tel:9129152311" className="text-brand-red font-semibold link-underline">
                    912-915-2311
                  </a>
                </div>
              </div>
            </div>
          </Section>
        </div>
      </div>
      <Footer />
    </main>
  );
};

export default Terms;
